import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiFillEye } from 'react-icons/ai'
import { HiPlus } from 'react-icons/hi'
import { HiOutlineArchiveBox, HiOutlineTicket } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'
import { useAsyncDebounce } from 'react-table'

import {
  useGetHelpdeskCategoriesQuery,
  useGetHelpdeskPrioritiesQuery,
  useGetHelpdeskStatusesQuery,
  useGetMyClosedHelpdeskTicketsQuery,
  useGetMyHelpdeskTicketsQuery,
} from '../../api/services/helpdeskApi'
import CreateTicketModal from '../../components/helpdesk/CreateTicketModal'
import HelpDeskEmailSettingsToggle from '../../components/helpdesk/HelpDeskEmailSettingsToggle'
import NewTable from '../../components/table/NewTable'
import TableWrapper from '../../components/table/TableWrapper'
import { getHelpDeskTicketsColumns } from './helpDeskTicketsColumns'

const HelpDeskTickets = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filterInputs, setFilterInputs] = useState({})
  const [activeTab, setActiveTab] = useState('active')
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const archive = activeTab === 'closed'

  const { data: categoriesResponse } = useGetHelpdeskCategoriesQuery()
  const { data: prioritiesResponse } = useGetHelpdeskPrioritiesQuery()
  const { data: statusesResponse } = useGetHelpdeskStatusesQuery()

  const handleFilterChange = useAsyncDebounce((value, columnId) => {
    setPage(1)
    setFilterInputs((previous) => ({ ...previous, [columnId]: value.trim() || ' ' }))
  }, 50)

  const query = useMemo(() => {
    const params = new URLSearchParams()
    Object.entries(filterInputs).forEach(([key, value]) => {
      if (value.trim()) params.set(key, value)
    })
    return params.toString()
  }, [filterInputs])

  const queryParams = { page, page_size: pageSize, query }
  const activeQuery = useGetMyHelpdeskTicketsQuery(queryParams, { skip: activeTab !== 'active' })
  const closedQuery = useGetMyClosedHelpdeskTicketsQuery(queryParams, { skip: activeTab !== 'closed' })

  const listQuery = activeTab === 'closed' ? closedQuery : activeQuery
  const data = listQuery.data
  const isLoading = listQuery.isLoading
  const isFetching = listQuery.isFetching

  const tickets = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data])

  const columns = useMemo(
    () =>
      getHelpDeskTicketsColumns({
        t,
        handleFilterChange,
        statuses: statusesResponse?.data ?? [],
        priorities: prioritiesResponse?.data ?? [],
        categories: categoriesResponse?.data ?? [],
        archive,
      }),
    [archive, categoriesResponse, handleFilterChange, prioritiesResponse, statusesResponse, t],
  )

  const tableHooksArray = useMemo(
    () => [
      {
        label: t('support.list.view'),
        Icon: AiFillEye,
        onClick: (row) => navigate(`/helpdesk/${row.original.ticket_id}`),
        disabled: false,
      },
    ],
    [navigate, t],
  )

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return
    setActiveTab(tabId)
    setPage(1)
    setFilterInputs({})
  }

  const tabs = [
    { id: 'active', label: t('support.tabs.active') },
    { id: 'closed', label: t('support.tabs.closed') },
  ]

  return (
    <div className="space-y-4 w-full">
      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#006EAB] text-white shadow-sm">
              <HiOutlineTicket className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 pt-0.5">
              <h1 className="text-xl font-semibold text-slate-900">{t('support.my_tickets')}</h1>
              <p className="mt-0.5 text-sm text-slate-500">{t('support.list.description')}</p>
            </div>
          </div>

          {!archive ? (
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 self-start rounded-lg bg-[#006EAB] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#005a8f] focus:outline-none focus:ring-2 focus:ring-[#006EAB]/30 sm:self-center"
            >
              <HiPlus className="h-4 w-4" />
              {t('support.new_ticket_button')}
            </button>
          ) : null}
        </div>

        <div className="mt-4 border-t border-slate-200/70 pt-4">
          <HelpDeskEmailSettingsToggle />
        </div>
      </div>

      <div className="rounded-xl border border-[#006EAB]/20 bg-white p-1.5 shadow-sm">
        <nav className="flex gap-1" role="tablist" aria-label={t('support.my_tickets')}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all sm:flex-none ${
                activeTab === tab.id
                  ? 'bg-[#006EAB] text-white shadow-sm'
                  : 'text-slate-600 hover:bg-sky-100 hover:text-[#006EAB]'
              }`}
            >
              {tab.id === 'active' ? (
                <HiOutlineTicket className="h-4 w-4" aria-hidden />
              ) : (
                <HiOutlineArchiveBox className="h-4 w-4" aria-hidden />
              )}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <TableWrapper>
        <NewTable
          key={activeTab}
          columns={columns}
          data={tickets}
          hooksArray={tableHooksArray}
          actions
          filtering
          isLoading={isLoading}
          isFetching={isFetching}
          setPage={setPage}
          pageManual={page}
          pageSize={pageSize}
          setPageAmount={setPageSize}
          controlledPageCount={data?.total_pages || 0}
          filterInputs={filterInputs}
          setFilterInputs={setFilterInputs}
          count={data?.count}
        />
      </TableWrapper>

      <CreateTicketModal isOpen={isCreateOpen} setIsOpen={setIsCreateOpen} />
    </div>
  )
}

export default HelpDeskTickets
